import {
  CONVERSION_ACTIONS,
  labelFor,
  SEND_OUTCOME,
  SEND_OUTCOME_LABEL,
  SKIP_REASON_LABEL
} from 'lib';
import React from 'react';
import {
  Badge,
  Btn,
  Empty,
  Failed,
  Field,
  Grid,
  GroupLabel,
  Inset,
  Kpi,
  Note,
  NotWired,
  PillRow,
  SelectPill,
  T,
  Table,
  TableScroll,
  TextInput
} from './ui';

/**
 * Section 7 - delivery reporting, and the per-recipient lookup that answers support tickets.
 *
 * ## A rate and an em dash mean different things, and must keep looking different
 *
 * The design shows Delivered, Open Rate, Click Rate and CTA Conversion. All four are measured:
 * opens from a marker P4P embeds and serves, clicks from P4P's tracked redirect, and CTA conversion
 * from an in-app action joined to a click. None of them depends on a mail provider's event feed.
 *
 * The rule that matters is the same one that predates the measurement: a card shows a number only
 * when there was something to measure. `null` renders as an em dash with the reason attached, and
 * `0.0%` is reserved for a campaign that was genuinely measured and genuinely not opened. A zero
 * under "Open Rate" does not read as "not measured" - it reads as "nobody opens our email", which
 * would be a false and expensive conclusion to hand an operator.
 *
 * That distinction is decided by the backend, which returns `null` rather than zero for an empty
 * denominator. This component's job is not to re-derive it, only to never flatten it.
 *
 * Two things the figures are not: they cover only the recipients it was lawful to measure, which is
 * why the measured-subset line sits under the row, and an open rate is approximate in both
 * directions - image blocking hides opens entirely, while clients that prefetch remote images for
 * privacy report opens no human made.
 */

/*
  Named for what an operator can and cannot ask, not for the function that answers it.

  This began "getCampaignStats aggregates one campaign's send records" — the name of an internal
  endpoint, in a tooltip, to an operator who cannot call it and would not recognise it.
*/
/*
  Seconds into the design's "12 mins".

  Rounded to whole units on purpose: "11.7 minutes" implies a precision the figure does not have,
  since it averages provider timestamps whose resolution is a second at best and whose population
  is only the recipients whose mail client loaded images.
*/
function formatDuration(seconds) {
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr`;
  return `${Math.round(hours / 24)} days`;
}

const SCOPE_NOTE =
  'These figures cover one campaign at a time. Totals across several campaigns — by team, country, segment or date range — are not available yet. A campaign sent before engagement was measured shows a dash rather than a zero for the three rates.';

const AnalyticsReports = ({
  campaigns = [],
  selectedId,
  onSelect,
  stats,
  loading,
  onRefresh,
  lookup,
  onLookupChange,
  onLookup,
  sends,
  searchedFor,
  lookupFailed,
  searching,
  busy
}) => {
  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id || campaign._id,
    label: `${campaign.name} - ${campaign.status}`
  }));

  const processed = stats?.recipientsProcessed || 0;
  const accepted = stats?.byOutcome?.[SEND_OUTCOME.ACCEPTED] ?? 0;
  const delivered = stats?.byOutcome?.[SEND_OUTCOME.DELIVERED] ?? 0;
  const bounced = stats?.byOutcome?.[SEND_OUTCOME.BOUNCED] ?? 0;
  const notSent =
    (stats?.byOutcome?.[SEND_OUTCOME.SUPPRESSED] ?? 0) +
    (stats?.byOutcome?.[SEND_OUTCOME.SKIPPED] ?? 0);

  const rate = (part) => (processed > 0 ? `${((part / processed) * 100).toFixed(1)}%` : '-');

  /*
    A rate arrives as a fraction, or as null when there was nothing to measure.

    `null` is passed straight through to the card, which renders it as an em dash. Coercing it to
    0 here - which `(value * 100).toFixed(1)` does silently for null - would turn "we are not
    measuring this" into "nobody opened it", which is the one mistake this screen exists to avoid.
  */
  const engagement = stats?.engagement || {};
  const asPercent = (value) => (typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : null);

  const openRate = asPercent(engagement.openRate);
  const clickRate = asPercent(engagement.clickRate);
  const conversionRate = asPercent(engagement.conversionRate);

  const openSeconds = engagement.averageSecondsToFirstOpen;
  /*
    The caveat names both directions, because the figure errs both ways and is about to be read as
    authoritative now that it is P4P's own rather than a provider's.

    It used to say only "understates readership", which was true while the number could only be
    missed opens. A marker on our own domain is also fetched by clients that proxy and prefetch
    remote images for privacy - an open no human made. Fetches inside ten seconds of the send are
    excluded, which catches scanners and some prefetching, not all of it.

    Saying so costs a longer label. Not saying so invites an operator to act on a difference of two
    points between campaigns, which this number cannot support.
  */
  const openSub =
    typeof openSeconds === 'number'
      ? `Avg. time to open: ${formatDuration(openSeconds)}. Approximate - blocked images hide opens, prefetching clients add ones nobody made.`
      : 'Approximate - blocked images hide opens, prefetching clients add ones nobody made';

  // "Nobody converted" and "nobody said what converting means" are different answers, and only one
  // of them is about fans.
  const conversionNote = engagement.conversionAction
    ? 'Conversions are not measured yet. They need click tracking to join the in-app action to.'
    : 'No conversion action is set for this campaign, so there is nothing to count. Set one when editing the campaign.';

  /*
    The operator's wording for the action, not the stored value.

    Printing `conversionAction` raw put an identifier like `challengeEntered` on a reporting card,
    in a vocabulary only the codebase uses. The composer already labels every action; this reads the
    same list so the two screens cannot describe one thing two ways.
  */
  const conversionActionLabel = (
    CONVERSION_ACTIONS.find((action) => action.value === engagement.conversionAction)?.label ??
    engagement.conversionAction ??
    ''
  ).toLowerCase();

  const measurable = engagement.measurable ?? 0;
  const notTracked = engagement.notTracked ?? 0;

  /*
    The excluded recipients, split by the cause recorded when each message went out.

    Additive on the wire: `notTracked` still carries the total, so this renders as an empty string
    against a campaign sent before the reasons were recorded rather than as a row of zeroes claiming
    nobody was excluded for anything.
  */
  const reasons = engagement.notTrackedReasons ?? {};
  const unknownLocation = reasons.unknownLocation ?? 0;
  const consentWithheld = reasons.consentWithheld ?? 0;
  const categoryExcluded = reasons.categoryExcluded ?? 0;

  const notTrackedBreakdown = [
    unknownLocation > 0 ? `${unknownLocation.toLocaleString()} with an unknown location` : null,
    consentWithheld > 0 ? `${consentWithheld.toLocaleString()} without consent` : null,
    categoryExcluded > 0 ? `${categoryExcluded.toLocaleString()} never measurable` : null
  ]
    .filter(Boolean)
    .join(', ');

  /*
    A configuration fault and a quiet audience produce the same number, and only one of them is
    about fans.

    Still judged separately for opens and clicks, though the reason changed: both now come from
    P4P - opens from an embedded marker, clicks from the tracked redirect - so neither depends on a
    provider any more. They remain two independent paths with two endpoints, and one can be dead
    while the other is fine, so reporting a single "measurement is broken" would still send someone
    to check the wrong half.
  */
  /*
    Two states, and it used to be three.

    The third read "opens are not collected the way email is currently sent - this is a provider
    setting". That was true while opens arrived only from a mail provider's event feed: two of the
    four transports report none, so a campaign under those looked identically silent to one nobody
    read, and blaming the audience would have been wrong.

    P4P embeds its own marker now and serves its own endpoint, so opens are collected under every
    transport. The sentence is false, the field behind it (`openTrackingSupported`) is gone from the
    response, and both are removed here rather than left to read as history.

    What remains is the per-campaign heuristic, and it now points somewhere a person can actually
    go: silence across thousands of delivered messages means P4P's marker or the endpoint serving
    it, not a provider account nobody here can see.
  */
  const openTrackingBroken = engagement.openTrackingAvailable === false;
  const clickTrackingBroken = engagement.clickTrackingAvailable === false;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <GroupLabel>Interactive scope filters</GroupLabel>
        <PillRow>
          <SelectPill
            label="By Campaign"
            value={selectedId || ''}
            options={[{ value: '', label: 'Choose a campaign' }, ...campaignOptions]}
            onChange={(value) => onSelect(value || null)}
            disabled={campaignOptions.length === 0}
          />
          {/*
            The design's other four scope filters, with its own wording as the value so the row
            reads as designed - and disabled, with one marker for the row rather than four repeats
            of the same word where four different values belong.
          */}
          {[
            ['By Team', 'All Teams'],
            ['By Country', 'All Territories'],
            ['By Segment', 'All Segments'],
            ['By Date Range', 'All Time']
          ].map(([label, value]) => (
            <SelectPill
              key={label}
              label={label}
              value="all"
              disabled
              options={[{ value: 'all', label: value }]}
              onChange={() => {}}
            />
          ))}
          <NotWired note={SCOPE_NOTE} />
          <Btn
            variant="outline"
            icon="Refresh"
            onClick={onRefresh}
            disabled={!selectedId || loading}
          >
            {loading ? 'Reading...' : 'Refresh Metrics'}
          </Btn>
        </PillRow>
        <Note>{SCOPE_NOTE}</Note>
      </div>

      {!selectedId ? (
        <Empty title="Pick a campaign to see what happened to it">
          Figures come from the send records themselves, so they are available while a campaign is
          still going out rather than only once it finishes.
        </Empty>
      ) : loading ? (
        <Note>Reading send records...</Note>
      ) : !stats ? (
        <Empty title="No send records for this campaign yet">
          A campaign that has not started sending has nothing to report. Its status is in the
          campaign list on Setup & Automation, beside the scheduler.
        </Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Grid cols="repeat(auto-fit, minmax(200px, 1fr))" gap="14px">
            <Kpi
              label="Delivered"
              value={delivered > 0 ? delivered.toLocaleString() : accepted.toLocaleString()}
              tone={T.green}
              sub={
                delivered > 0
                  ? `${rate(delivered)} confirmed by the provider`
                  : `${rate(accepted)} accepted by the transport - no provider confirmation yet`
              }
            />
            <Kpi
              label="Open Rate"
              value={openRate}
              notWired={
                openRate === null
                  ? 'Nothing measurable in this campaign - it was sent before opens were measured, or no recipient could lawfully be measured.'
                  : undefined
              }
              sub={
                openRate === null
                  ? 'Not measured - a 0% here would read as nobody opening the email'
                  : openSub
              }
            />
            <Kpi
              label="Click Rate"
              value={clickRate}
              notWired={
                clickRate === null
                  ? "Clicks are not measured yet. They need P4P's own tracked links, so a fan never sees a provider tracking domain."
                  : undefined
              }
              sub={clickRate === null ? 'Not measured' : 'Of total unique delivered emails'}
            />
            <Kpi
              label="CTA Conversion"
              value={conversionRate}
              notWired={conversionRate === null ? conversionNote : undefined}
              /*
                "After a click" was true and not the whole truth.

                Entering a FanCam Challenge happens in the app, so between the click and the
                conversion sit an app install and a sign-in — two steps the email does not control
                and this figure cannot distinguish. A card reading "3% — enters the fancam challenge
                after a click" invites reading a low number as weak copy, when it may be a fan who
                read the email, wanted in, and did not have the app.

                So the sub-label names the step that actually has to happen. It does not excuse the
                number; it says what the number is of.
              */
              sub={
                conversionRate === null
                  ? 'Not measured'
                  : `Clicked, then completed in the app: ${conversionActionLabel}`
              }
            />
          </Grid>

          {/*
            What the rates above actually cover.

            A 24% open rate across a fully measurable audience and a 24% rate across the consented
            third of one are different facts, and a bare percentage presents them identically. The
            rates exclude untracked recipients from both sides, so without this line an operator has
            no way to tell how much of their audience the number speaks for.
          */}
          {openTrackingBroken || clickTrackingBroken ? (
            <Note style={{ color: T.red }}>
              {openTrackingBroken && clickTrackingBroken
                ? 'Neither opens nor clicks were recorded for a campaign this size. Treat the rates below as a configuration problem rather than as fan behaviour.'
                : openTrackingBroken
                  ? 'Clicks were recorded but no opens were, which is not how audiences behave. Check that the open marker is reachable - the same kind of fault as a dead tracked link, on P4P rather than at the email provider.'
                  : 'Opens were recorded but no clicks were. Check that the tracked-link redirect is reachable.'}
            </Note>
          ) : null}

          {notTracked > 0 ? (
            <Note>
              These rates cover the {measurable.toLocaleString()} recipient
              {measurable === 1 ? '' : 's'} it was lawful to measure. {notTracked.toLocaleString()}{' '}
              more {notTracked === 1 ? 'was' : 'were'} sent without tracking, and{' '}
              {notTracked === 1 ? 'is' : 'are'} counted in neither the numerator nor the denominator
              {notTrackedBreakdown ? ': ' : '.'}
              {notTrackedBreakdown}
            </Note>
          ) : null}

          {/*
            An unreadable location is a defect, not an answer, and it is worth saying so.

            The sentence above used to offer both causes at once - "no consent recorded where consent
            is required, or an unknown location" - which left an operator unable to tell a data
            problem they could fix from an audience that had declined. These are different actions:
            one is a migration, the other is nothing at all.
          */}
          {unknownLocation > 0 ? (
            <Note style={{ color: T.amber }}>
              {unknownLocation.toLocaleString()} of those {unknownLocation === 1 ? 'has' : 'have'}{' '}
              no readable country on their account, so no region rule could be applied. That is a
              data problem rather than a fan declining, and fixing it is what moves those recipients
              into the rates.
            </Note>
          ) : null}

          <Grid cols="repeat(auto-fit, minmax(200px, 1fr))" gap="14px">
            <Kpi
              label="Audience estimated"
              value={(stats.estimatedRecipients ?? 0).toLocaleString()}
              sub="Resolved when the campaign was created"
            />
            <Kpi
              label="Processed so far"
              value={processed.toLocaleString()}
              sub={`Status: ${stats.status}`}
            />
            <Kpi
              label="Bounced"
              value={bounced.toLocaleString()}
              tone={bounced > 0 ? T.red : undefined}
              sub="A hard bounce suppresses that address everywhere"
            />
            <Kpi
              label="Not sent"
              value={notSent.toLocaleString()}
              tone={notSent > 0 ? T.amber : undefined}
              sub="Suppressed or skipped by policy - reasons below"
            />
          </Grid>

          <Grid cols="1fr 1fr" gap="14px">
            {Object.keys(stats.bySkipReason || {}).length > 0 ? (
              <Inset>
                <GroupLabel>Why fans were not sent to</GroupLabel>
                {Object.entries(stats.bySkipReason).map(([reason, count]) => (
                  <div
                    key={reason}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      fontSize: 13
                    }}
                  >
                    <span style={{ color: T.body }}>{labelFor(SKIP_REASON_LABEL, reason)}</span>
                    <strong style={{ color: T.heading }}>{count.toLocaleString()}</strong>
                  </div>
                ))}
              </Inset>
            ) : null}

            <Inset>
              <GroupLabel>Every outcome recorded</GroupLabel>
              {Object.entries(stats.byOutcome || {}).map(([outcome, count]) => (
                <div
                  key={outcome}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    fontSize: 13
                  }}
                >
                  <span style={{ color: T.body }}>{labelFor(SEND_OUTCOME_LABEL, outcome)}</span>
                  <strong style={{ color: T.heading }}>{count.toLocaleString()}</strong>
                </div>
              ))}
            </Inset>
          </Grid>
        </div>
      )}

      {/*
        The per-recipient lookup.

        Not in the design, kept because it is the one thing that answers the ticket the design
        cannot: "my verification email never arrived". SC-011 asks that to be answerable in under
        two minutes by someone who has never read this codebase, and aggregate rates never will be.
      */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          borderTop: `1px solid ${T.border}`,
          paddingTop: 18
        }}
      >
        <GroupLabel>Look up one fan&apos;s email history</GroupLabel>
        {/*
          The button in the address box's own row, the height of the box.

          It stood beside the whole field, bottom-aligned, and the field's bottom is its hint — so
          "Find sends" sat level with the hint text, below and shorter than the box it searches.
        */}
        <Field
          label="Recipient address"
          hint="Every send to this address, newest first, with why it was or was not sent."
          action={
            <Btn onClick={onLookup} disabled={busy || !lookup}>
              {searching ? 'Searching…' : 'Find sends'}
            </Btn>
          }
        >
          <TextInput
            type="email"
            value={lookup}
            placeholder="fan@example.com"
            onChange={(event) => onLookupChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onLookup();
            }}
          />
        </Field>

        {/*
          Three outcomes, three answers.

          This was one ternary against `sends.sends.length` with `null` on the empty side, so a
          failed request, an address with no sends, and a box nobody had searched yet all rendered
          nothing at all — on the panel whose entire purpose is answering "did this fan get the
          email?". A blank panel reads as "no", and "no" is a different answer from "the query
          failed".
        */}
        {lookupFailed ? (
          <Failed title="The lookup failed" onRetry={onLookup} busy={busy}>
            This is not an address with no sends — the request itself did not come back. Nothing can
            be concluded about {searchedFor || 'this address'} from it.
          </Failed>
        ) : sends?.sends?.length > 0 ? (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Template</th>
                  <th>Outcome</th>
                  <th>Reason</th>
                  <th>Provider id</th>
                </tr>
              </thead>
              <tbody>
                {sends.sends.map((send) => (
                  <tr key={send._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {send.createdAt ? new Date(send.createdAt).toLocaleString() : '-'}
                    </td>
                    <td>{send.templateKey}</td>
                    <td>
                      <Badge
                        tone={
                          send.outcome === SEND_OUTCOME.DELIVERED ||
                          send.outcome === SEND_OUTCOME.ACCEPTED
                            ? 'green'
                            : send.outcome === SEND_OUTCOME.BOUNCED ||
                                send.outcome === SEND_OUTCOME.FAILED
                              ? 'red'
                              : 'amber'
                        }
                      >
                        {labelFor(SEND_OUTCOME_LABEL, send.outcome)}
                      </Badge>
                    </td>
                    <td>{labelFor(SKIP_REASON_LABEL, send.skipReason) || '-'}</td>
                    <td style={{ color: T.faint, fontSize: 12 }}>
                      {send.providerMessageId || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        ) : searchedFor ? (
          <Empty title={`No email has been sent to ${searchedFor}`}>
            The address is not in the send records at all, which is a different finding from a send
            that failed: nothing was ever attempted. Check the spelling, then whether the account
            exists — a fan who never registered has no send history to have.
          </Empty>
        ) : null}
      </div>
    </div>
  );
};

export default AnalyticsReports;
